import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import UserModal from "./Components/UserModal";
import api from "../../api";
import "../../styles/Login.scss";

interface User {
    id: number;
    name: string;
    email: string;
    role: string;
    created_at: string;
}

const AdminUsers = () => {
    const navigate = useNavigate();

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    useEffect(() => {
        const storedUser = JSON.parse(
            localStorage.getItem("user") ||
            sessionStorage.getItem("user") ||
            "{}"
        );

        if (storedUser.role !== "admin") {
            navigate("/admin-dashboard");
            return;
        }

        fetchUsers();
    }, [navigate]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get("/admin/users");

            setUsers(response.data.data || response.data);
        } catch (err: any) {
            console.error("Failed to load users:", err);

            if (err.response?.status === 403) {
                navigate("/admin-dashboard");
                return;
            }

            setError(
                err.response?.data?.message ||
                "Unable to load users. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = () => {
        setSelectedUser(null);
        setShowModal(true);
    };

    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedUser(null);
    };

    return (
        <div className="admin-page">

            <AdminHeader />

            <div className="admin-body">

                <AdminSidebar />

                <main className="admin-content">

                    <div className="admin-users">

                        <div className="admin-page-header">

                            <div>
                                <h1>Users</h1>

                                <p>
                                    Manage administrator and editor accounts.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="admin-primary-button"
                                onClick={handleAddUser}
                            >
                                Add user
                            </button>

                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        <div className="admin-users-table">

                            {loading ? (
                                <p>Loading users...</p>
                            ) : users.length === 0 ? (
                                <p>No users found.</p>
                            ) : (
                                <table>

                                    <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id}>

                                            <td>
                                                {user.name}
                                            </td>

                                            <td>
                                                {user.email}
                                            </td>

                                            <td>
                                                <span
                                                    className={`admin-role admin-role-${user.role}`}
                                                >
                                                    {user.role}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="admin-user-actions">

                                                    <button
                                                        type="button"
                                                        className="admin-table-button"
                                                        onClick={() =>
                                                            handleEditUser(user)
                                                        }
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="admin-table-button admin-table-button-danger"
                                                    >
                                                        Delete
                                                    </button>

                                                </div>
                                            </td>

                                        </tr>
                                    ))}
                                    </tbody>

                                </table>
                            )}

                        </div>

                    </div>

                </main>

            </div>

            {showModal && (
                <UserModal
                    user={selectedUser}
                    onClose={handleCloseModal}
                    onSaved={fetchUsers}
                />
            )}

        </div>
    );
};

export default AdminUsers;
